import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Grid, Pagination } from "swiper/modules";
import { useGetProductsQuery } from "../../templates/ecommerce/product/api/productApi";
import { useAppDispatch } from "../../common/hooks/hooks";
import { openProductModal } from "../../templates/ecommerce/product/components/common/ProductModal/productModalSlice";
import ProductCard from "../../templates/ecommerce/product/components/common/ProductCard/ProductCard";
import ProductCardSkeleton from "../../templates/ecommerce/product/components/common/ProductCard/ProductCardSkeleton";
import { GetProductsParams } from "../../templates/ecommerce/product/types/productApiTypes";

import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";

type TabKey = "on_sale" | "featured" | "new_arrivals";

const FeaturedProductsSlider: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<TabKey>("on_sale");

  const newArrivalsTagId = window.commonVariables?.newArrivalsTagId || 0;

  const tabs = [
    {
      key: "on_sale" as TabKey,
      label: t("home.featuredSlider.onSale", "On Sale"),
    },
    {
      key: "featured" as TabKey,
      label: t("home.featuredSlider.featured", "Best Seller"),
    },
    {
      key: "new_arrivals" as TabKey,
      label: t("home.featuredSlider.newArrivals", "New Arrivals"),
    },
  ];

  const queryParams: GetProductsParams = useMemo(() => {
    const baseParams: GetProductsParams = {
      // PERFORMANCE FIX: Cap at 24.
      // Divisible by 2 (mobile), 3 (tablet), 4 (desktop).
      limit: 24,
    };

    switch (activeTab) {
      case "on_sale":
        return { ...baseParams, onSale: true };
      case "featured":
        return { ...baseParams, featured: true, orderby: "menu_order" };
      case "new_arrivals":
        return {
          ...baseParams,
          orderby: "date",
          tag: newArrivalsTagId ? [newArrivalsTagId] : [0],
        };
      default:
        return baseParams;
    }
  }, [activeTab, newArrivalsTagId]);

  const { data, isLoading, isFetching } = useGetProductsQuery(queryParams);
  const products = data?.products || [];

  const handleQuickView = (id: number) => {
    dispatch(openProductModal({ productId: id }));
  };

  const isBusy = isLoading || isFetching;

  return (
    <>
      <div className="fps-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`fps-tab ${activeTab === tab.key ? "is-active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
            disabled={isBusy && activeTab !== tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={`fps-content ${isBusy ? "is-loading" : ""}`}>
        {isLoading && products.length === 0 ? (
          <div className="fps-grid-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : products.length > 0 ? (
          <Swiper
            modules={[Grid, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            grid={{ rows: 2, fill: "row" }}
            pagination={{ clickable: true }}
            // PERFORMANCE SETTINGS
            observer={true} // Re-init swiper if DOM changes
            observeParents={true}
            watchSlidesProgress={true} // Only render visible slides properly
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 15,
                grid: { rows: 2, fill: "row" },
              },
              540: {
                slidesPerView: 2,
                spaceBetween: 20,
                grid: { rows: 2, fill: "row" },
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 20,
                grid: { rows: 2, fill: "row" },
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 30,
                grid: { rows: 2, fill: "row" },
              },
            }}
            className="fps-swiper"
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <ProductCard
                  product={product}
                  view="grid"
                  onQuickViewClick={handleQuickView}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="fps-empty">
            {t("home.featuredSlider.noProducts", "No products found.")}
          </div>
        )}
      </div>
    </>
  );
};

export default FeaturedProductsSlider;
